import { useEffect, useRef } from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import closeMsgImg from "../../asserts/img/closeX.svg";
import Text from "../Text";
import IconButton from "../IconButton";
import { useMobile } from "../../utils/mobile";

const StyledModal = styled("div")(() => ({
  position: "fixed",
  zIndex: 1102,
  right: 0,
  top: 0,
  left: 0,
  bottom: 0,
  width: "100%",
  height: "100%",
  overflow: "auto",
  display: "none",
  backgroundColor: "rgba(0,85,164, 0.8)",
}));

const ModalContent = styled("div")(({ theme }) => ({
  backgroundColor: theme.color.white,
  opacity: 1,
  margin: "auto",
  padding: 5,
  borderRadius: 7,
  textAlign: "center",
  display: "flex",
  alignItems: " center",
}));

// Open modals, newest last: Esc closes only the top one
const openModals = [];
let nextModalId = 1;

const useCloseOnEscape = (isOpen, onClose) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    const modalId = nextModalId++;
    openModals.push(modalId);

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && openModals[openModals.length - 1] === modalId) {
        onCloseRef.current?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      openModals.splice(openModals.indexOf(modalId), 1);
    };
  }, [isOpen]);
};

const Modal = ({ children, show, onClose, title, width }) => {
  const theme = useTheme();
  const isMobile = useMobile();
  const { t } = useTranslation("common");
  const isOpen = show !== false;
  const pressStartedOnBackdrop = useRef(false);

  useCloseOnEscape(isOpen, onClose);

  if (!isOpen) {
    return null;
  }

  // Close only for a press that started and ended on the backdrop itself, so selecting text
  // inside the modal and releasing outside of it doesn't close it
  const handleBackdropMouseDown = (event) => {
    pressStartedOnBackdrop.current = event.target === event.currentTarget;
  };

  const handleBackdropClick = (event) => {
    if (pressStartedOnBackdrop.current && event.target === event.currentTarget) {
      onClose();
    }
    pressStartedOnBackdrop.current = false;
  };

  return (
    <StyledModal
      style={{ display: "flex" }}
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}>
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            margin: 20,
            width,
          }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.5rem"
            }}>
            <div>
              {!!title && <Text.H1 color={theme.color.label}>{title}</Text.H1>}
            </div>
            <IconButton
              src={closeMsgImg}
              label={t("close_dialog")}
              iconSize={isMobile ? 20 : 14}
              onClick={() => onClose()}
            />
          </div>
          <div>{children}</div>
        </div>
      </ModalContent>
    </StyledModal>
  );
};

export default Modal;
